import { useState } from "react";
import { CoursesTable } from "../courses-table";
import { Modal, Space, Tooltip, notification } from "antd";
import { Button } from "react-bootstrap";
import {
  HiOutlineSquare2Stack,
  HiOutlineTrash,
  HiPencilSquare,
} from "react-icons/hi2";
import type { ColumnsType } from "antd/es/table";
import { produce } from "immer";
import moment from "moment";

import UploadExcelFile from "../../../../../../components/input/arrayData/upload-excel-file";
import DownloadExcelTemplate from "../../../../../../components/input/arrayData/DownloadExcelTemplate";
import { StringHelper } from "../../../../../../helper";
import { CreateCourseForm } from "../create-course-form";
import { DateFormat } from "../../../../../../constants";
import { useCourseColumns } from "../../hooks";
import { ConfirmModal } from "../../../../../../components";

interface CoursesActionTableProps {
  value: any[];
  onChange: (value: any[]) => void;
}

export const CoursesActionTable = (props: Partial<CoursesActionTableProps>) => {
  const { value = [], onChange = () => {} } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const { columns } = useCourseColumns();

  const onCloseModal = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  const onSubmit = (values: any) => {
    const newValues = produce(value, (draft) => {
      if (!values?.key) {
        draft.push({
          key: StringHelper.random(),
          eTrainees: [],
          eLessonTypes: [],
          eCoursePlaces: [],
          ...values,
        });
        return;
      }
      const courseIndex = draft.findIndex((e: any) => e.key === values.key);
      draft[courseIndex] = values;
    });
    onChange(newValues);
    onCloseModal();
  };

  const onDuplicate = (record: any) => {
    const newValues = produce(value, (draft) => {
      const recordIndex = draft.findIndex((e: any) => e.key === record.key);
      const course = value[recordIndex];
      const newCourse = { ...course, key: StringHelper.random() };
      draft.splice(recordIndex + 1, 0, newCourse);
    });
    onChange(newValues);
  };
  const onEdit = (record: any) => {
    const course = value.find((e: any) => e.key === record.key);
    setSelectedItem(course);
    setTimeout(() => setIsModalOpen(true), 0);
  };
  const onRemove = (keys: string[]) => {
    onChange(value.filter((e) => !keys.includes(e.key)));
  };

  const onImportFileChange = (values: any = []) => {
    try {
      const importedCourses = values.reduce((acc: any[], current: any) => {
        const { eDateFrom, eDateTo, ...rest } = current;
        let body = {
          ...rest,
          key: StringHelper.random(),
          eCoursePlaces: [],
          eLessonTypes: [],
          eTrainees: [],
        };

        if (eDateFrom && eDateTo) {
          if (eDateFrom?.isValid === false || eDateTo?.isValid === false) {
            throw new Error("");
          }
          const fromDateMoment = moment(eDateFrom, DateFormat.DD_MM_YYYY);
          const toDateMoment = moment(eDateTo, DateFormat.DD_MM_YYYY);
          if (!fromDateMoment.isValid() || !toDateMoment.isValid()) {
            throw new Error("");
          }
          body = { ...body, eDateTime: [fromDateMoment, toDateMoment] };
        } else {
          throw new Error("");
        }
        return [...acc, body];
      }, []);

      onChange([...value, ...importedCourses]);
    } catch (error) {
      notification.error({
        message: "Thất bại",
        description: "Nhập liệu thất bại. Vui lòng kiểm tra lại dữ liệu",
      });
    }
  };

  const createColumns: ColumnsType<any> = [
    {
      key: "action",
      dataIndex: "action",
      width: 30,
      align: "left" as const,
      render: (_: string, record: any) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <HiPencilSquare
              size={20}
              className="cursor-pointer"
              color="#0ea5e9"
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Nhân bản">
            <HiOutlineSquare2Stack
              size={20}
              className="cursor-pointer"
              color="#22c55e"
              onClick={() => onDuplicate(record)}
            />
          </Tooltip>
          <Tooltip title="Xoá">
            <HiOutlineTrash
              size={20}
              className="cursor-pointer"
              color="#dc2626"
              onClick={() => onRemove([record.key])}
            />
          </Tooltip>
        </Space>
      ),
    },
    ...columns,
  ];

  return (
    <div>
      <Space style={{ marginBottom: 10 }}>
        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
          Thêm mới
        </Button>
        <ConfirmModal
          onOk={() => {
            onRemove(selectedRowKeys);
            setSelectedRowKeys([]);
          }}
        >
          <Button disabled={!Boolean(selectedRowKeys.length)} variant="danger">
            Xoá nhiều
          </Button>
        </ConfirmModal>
        <UploadExcelFile
          template="monthly-training-plan"
          onChange={onImportFileChange}
        >
          <Button variant="success">Tải biểu mẫu</Button>
        </UploadExcelFile>
        <DownloadExcelTemplate
          className="download-excel-btn"
          template="monthly-training-plan"
        />
      </Space>
      <CoursesTable
        dataSource={value}
        columns={createColumns}
        rowSelection={{
          selectedRowKeys,
          onChange: (newSelectedRowKeys: any[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
          },
        }}
        scroll={{ x: 2000 }}
      />
      <Modal
        className="modal-full"
        title={
          <div style={{ fontSize: 18 }}>
            {selectedItem ? "Chỉnh sửa khoá đào tạo" : "Thêm khoá đào tạo"}
          </div>
        }
        open={isModalOpen}
        footer={null}
        onCancel={onCloseModal}
        destroyOnClose
      >
        <CreateCourseForm
          onSubmit={onSubmit}
          course={selectedItem}
          hiddenFields={[
            "eTrainees",
            "eCoursePlaces",
            "eLessonTypes",
            "eCourseTrainee",
          ]}
        />
      </Modal>
    </div>
  );
};
